// User Activity Types
// LazySEM Frontend - Activity tracking types

export interface UserActivity {
  id: string;
  user_id: string;
  organization_id?: string;
  activity_type: string;
  activity_title: string;
  activity_description?: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  ip_address?: string;
  user_agent?: string;
  activity_metadata: {
    service?: string;
    category?: string;
    [key: string]: any;
  };
  created_at: string;
}

export interface ActivityStats {
  total_activities: number;
  categories: Record<string, number>;
  most_recent?: UserActivity;
}

export interface ActivityResponse {
  activities: UserActivity[];
  total: number;
  limit: number;
  offset: number;
}

// Activity categories
export type ActivityCategory = 
  | 'authentication' 
  | 'analysis' 
  | 'project' 
  | 'monitoring' 
  | 'file' 
  | 'organization' 
  | 'api' 
  | 'security';

// Activity filters
export interface ActivityFilters {
  limit?: number;
  offset?: number;
  organization_id?: string;
  activity_types?: string[];
  categories?: ActivityCategory[];
}

// Activity type definitions by service
export const ACTIVITY_TYPES = {
  // Authentication Service
  AUTH: {
    LOGIN: 'login',
    LOGOUT: 'logout',
    REGISTER: 'register',
    PASSWORD_CHANGE: 'password_change',
    MFA_ENABLE: 'mfa_enable',
    MFA_DISABLE: 'mfa_disable',
    PROFILE_UPDATE: 'profile_update',
    AVATAR_CHANGE: 'avatar_change',
    ORG_CREATE: 'org_create',
    ORG_JOIN: 'org_join',
    ORG_LEAVE: 'org_leave',
    API_KEY_CREATE: 'api_key_create',
    API_KEY_DELETE: 'api_key_delete',
  },
  
  // Briefing Service
  BRIEFING: {
    PROJECT_CREATE: 'briefing_project_create',
    PROJECT_UPDATE: 'briefing_project_update',
    PROJECT_DELETE: 'briefing_project_delete',
    ANALYSIS_CREATE: 'briefing_analysis_create',
    ANALYSIS_COMPLETE: 'briefing_analysis_complete',
    ANALYSIS_DOWNLOAD: 'briefing_analysis_download',
    ANALYSIS_VIEW: 'briefing_analysis_view',
  },
  
  // Similarity Service
  SIMILARITY: {
    PROJECT_CREATE: 'similarity_project_create',
    PROJECT_UPDATE: 'similarity_project_update',
    PROJECT_DELETE: 'similarity_project_delete',
    JOB_CREATE: 'similarity_job_create',
    JOB_DELETE: 'similarity_job_delete',
    FILE_UPLOAD: 'similarity_file_upload',
    RESULTS_DOWNLOAD: 'similarity_results_download',
    ANALYSIS_COMPLETE: 'similarity_analysis_complete',
  },
  
  // Monitoring Service
  MONITORING: {
    PROJECT_CREATE: 'monitoring_project_create',
    PROJECT_UPDATE: 'monitoring_project_update',
    PROJECT_DELETE: 'monitoring_project_delete',
    CRAWL_START: 'monitoring_crawl_start',
    CRAWL_COMPLETE: 'monitoring_crawl_complete',
    CHANGES_VIEW: 'monitoring_changes_view',
    CHANGE_ACKNOWLEDGE: 'monitoring_change_acknowledge',
    PROJECT_PAUSE: 'monitoring_project_pause',
    PROJECT_RESUME: 'monitoring_project_resume',
  },
} as const;

// Helper function to get activity category from type
export const getActivityCategory = (activityType: string): ActivityCategory => {
  if (activityType.startsWith('briefing_analysis') || activityType.startsWith('similarity_analysis')) {
    return 'analysis';
  }
  if (activityType.includes('project')) {
    return 'project';
  }
  if (activityType.startsWith('monitoring_')) {
    return 'monitoring';
  }
  if (activityType.includes('file') || activityType.includes('upload') || activityType.includes('download')) {
    return 'file';
  }
  if (activityType.includes('org_') || activityType.includes('organization')) {
    return 'organization';
  }
  if (activityType.includes('api_key')) {
    return 'api';
  }
  if (activityType.includes('mfa') || activityType.includes('password') || activityType.includes('security')) {
    return 'security';
  }
  return 'authentication';
};

// Helper function to get activity icon
export const getActivityIcon = (activityType: string): string => {
  const category = getActivityCategory(activityType);
  
  switch (category) {
    case 'analysis':
      return '📊';
    case 'project':
      return '📁';
    case 'monitoring':
      return '👁️';
    case 'file':
      return '📎';
    case 'organization':
      return '🏢';
    case 'api':
      return '🔑';
    case 'security':
      return '🔒';
    case 'authentication':
    default:
      return '👤';
  }
};

// Helper function to get activity color
export const getActivityColor = (activityType: string): string => {
  const category = getActivityCategory(activityType);
  
  switch (category) {
    case 'analysis':
      return '#3b82f6'; // blue
    case 'project':
      return '#10b981'; // emerald
    case 'monitoring':
      return '#f59e0b'; // amber
    case 'file':
      return '#8b5cf6'; // violet
    case 'organization':
      return '#06b6d4'; // cyan
    case 'api':
      return '#ef4444'; // red
    case 'security':
      return '#f97316'; // orange
    case 'authentication':
    default:
      return '#6b7280'; // gray
  }
};
